import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import type { SlideEditor } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-entry/edit-pc/slide-editor/index';

/**
 * 缓存 SlideEditor.
 * 命中该缓存的 SlideEditor 不走销毁重建逻辑.
 */
export class SlideEditorCache {
    private cache: Map<ISlideModel, SlideEditor>;
    private readonly capacity: number;
    private readonly handleEditorCacheDelete?: (slideEditor: SlideEditor) => void;
    private getReserveSlideIndexRange?: () => string[];

    public constructor(
        capacity: number,
        handleEditorCacheDelete?: (slideEditor: SlideEditor) => void,
        getReserveSlideIndexRange?: () => string[],
    ) {
        this.cache = new Map();
        this.capacity = capacity;
        this.handleEditorCacheDelete = handleEditorCacheDelete;
        this.getReserveSlideIndexRange = getReserveSlideIndexRange;

        document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }

    public dispose() {
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        this.cache.forEach((slideEditor) => {
            this.deleteCache(slideEditor);
        });
    }

    public shouldCacheEditor(slideEditor: SlideEditor): boolean {
        // 暂定策略，元素数量超过 100 时需要缓存，不走销毁重建逻辑
        return slideEditor.slide.elementNumInfo.totel > 100;
    }

    public cacheEditor(slideEditor: SlideEditor): void {
        const key = slideEditor.slide;
        if (this.cache.has(key)) {
            this.cache.delete(key);
        }
        if (this.cache.size >= this.capacity) {
            this.deleteCache(this.cache.keys().next().value);
        }
        this.cache.set(key, slideEditor);
    }

    public hasCache(slideEditor: SlideEditor): boolean {
        const key = slideEditor.slide;
        if (this.cache.has(key)) {
            // 访问一次则将其置为最新的
            const value = this.cache.get(key);
            this.cache.delete(key);
            this.cache.set(key, value as SlideEditor);
            return true;
        }
        return false;
    }

    public deleteCache(slideEditor: SlideEditor): void {
        const key = slideEditor.slide;
        if (this.cache.has(key)) {
            this.cache.delete(key);
            this.handleEditorCacheDelete?.(slideEditor);
        }
    }

    /**
     * 窗口隐藏时释放非可视区域内页面的缓存
     */
    private handleVisibilityChange = () => {
        if (document.hidden) {
            const slideKeyList = this.getReserveSlideIndexRange?.();
            if (!slideKeyList) {
                return;
            }

            this.cache.forEach((slideEditor, slideModel) => {
                if (slideKeyList.indexOf(slideModel.key) === -1) {
                    this.deleteCache(slideEditor);
                }
            });
        }
    };
}
